import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { likeOrUnlikePost } from "../../redux/actions/post";
import { server } from "../../server";
import { getStoredAuthToken } from "../../utils/authSession";
import SinglePostWidget from "../../widgets/SinglePostWidget";
import AuthenticationPrompt from "../Profile/AuthenticationPrompt";
import ProfileGalleryPostActionIcon from "./ProfileGalleryPostActionIcon";

const getCount = (...values) => {
  const value = values.find((candidate) => Number.isFinite(Number(candidate)));
  return Number(value || 0);
};

const getImageCaption = (image = {}) => String(
  image.postDescription
  || image.description
  || image.caption
  || image.comment
  || ""
).trim();

const getAvatarUrl = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return value.url || "";
};

const getImagePostId = (image = {}) => image.postId || image.sourcePostId || image.post?._id || image.post;

const getImageId = (image = {}) => image.imageId || image._id || image.id;

const getImageOwnerId = (image = {}) => (
  image.postOwnerId
  || image.ownerId
  || image.userId?._id
  || image.userId
  || image.shopId?._id
  || image.shopId
  || image.owner?._id
  || image.owner
);

const isShopProfileName = (value) => Boolean(
  value
  && value !== "User"
  && value !== "Guest"
  && value !== "defaultProfile"
);

const getCurrentProfileName = (storedProfile, reduxProfile, seller) => {
  const candidate = storedProfile && storedProfile !== "defaultProfile" && storedProfile !== "Guest"
    ? storedProfile
    : reduxProfile;

  if (candidate === "Shop") {
    return seller?.name || "Shop";
  }

  return candidate || "User";
};

const ProfileGalleryPostActions = ({
  image = {},
  ownerName = "MATRIX",
  ownerAvatar,
  profileType = "Profile",
  className,
  buttonClassName,
  ariaLabel = "Photo engagement",
}) => {
  const dispatch = useDispatch();
  const { user, currentProfile: reduxCurrentProfile } = useSelector((state) => state.user || {});
  const sellerState = useSelector((state) => state.seller || {});
  const seller = sellerState.activeSeller || sellerState.seller;
  const isLoggedIn = Boolean(user?._id || seller?._id);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [postModalMode, setPostModalMode] = useState(null);
  const [imageModalMode, setImageModalMode] = useState(null);
  const [imageComments, setImageComments] = useState(Array.isArray(image.comments) ? image.comments : []);
  const [commentDraft, setCommentDraft] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [imageShareDraft, setImageShareDraft] = useState("");
  const [isPublishingImageShare, setIsPublishingImageShare] = useState(false);
  const [publishedImagePostId, setPublishedImagePostId] = useState("");
  const [imageActionError, setImageActionError] = useState("");
  const postId = getImagePostId(image);
  const imageId = getImageId(image);
  const displayName = image.postOwnerName || image.ownerName || ownerName || profileType;
  const displayAvatar = getAvatarUrl(image.postOwnerAvatar || image.ownerAvatar || ownerAvatar);
  const activeProfileName = getCurrentProfileName(sessionStorage.getItem("currentProfile"), reduxCurrentProfile, seller);
  const activeProfileType = isShopProfileName(activeProfileName) ? "Shop" : "User";
  const activeProfile = activeProfileType === "Shop" ? seller : user;
  const activeProfileDisplayName = activeProfile?.name || activeProfileName || "MATRIX member";
  const activeProfileAvatar = getAvatarUrl(activeProfile?.avatar);
  const [isLiked, setIsLiked] = useState(Boolean(image.isLikedByCurrentUser || image.isLiked));
  const [likeCount, setLikeCount] = useState(getCount(image.likeCount, image.likes?.length));
  const [commentCount, setCommentCount] = useState(getCount(image.totalComments, image.commentCount, image.comments?.length));
  const [shareCount, setShareCount] = useState(getCount(image.shareCount, image.reshareCount));

  useEffect(() => {
    setIsLiked(Boolean(image.isLikedByCurrentUser || image.isLiked));
    setLikeCount(getCount(image.likeCount, image.likes?.length));
    setCommentCount(getCount(image.totalComments, image.commentCount, image.comments?.length));
    setShareCount(getCount(image.shareCount, image.reshareCount));
    setImageComments(Array.isArray(image.comments) ? image.comments : []);
    setImageShareDraft("");
    setPublishedImagePostId("");
    setImageActionError("");
  }, [image._id, image.isLiked, image.isLikedByCurrentUser, image.likeCount, image.likes?.length, image.totalComments, image.commentCount, image.comments, image.shareCount, image.reshareCount, postId]);

  const getAuthRequestConfig = () => ({
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${getStoredAuthToken() || ""}`,
      "current-profile": activeProfileName || "User",
    },
  });

  const applyImageEngagement = (data = {}) => {
    if (Number.isFinite(Number(data.likeCount))) {
      setLikeCount(Number(data.likeCount));
    }

    if (typeof data.isLikedByCurrentUser === "boolean") {
      setIsLiked(data.isLikedByCurrentUser);
    }

    if (Number.isFinite(Number(data.totalComments ?? data.commentCount))) {
      setCommentCount(Number(data.totalComments ?? data.commentCount));
    }

    if (Number.isFinite(Number(data.shareCount))) {
      setShareCount(Number(data.shareCount));
    }

    if (Array.isArray(data.comments)) {
      setImageComments(data.comments);
    }
  };

  const requireLogin = (action) => {
    if (!isLoggedIn) {
      setShowAuthPrompt(true);
      return;
    }

    action();
  };

  const handleLikePost = () => {
    if (!postId && !imageId) {
      return;
    }

    requireLogin(() => {
      const nextLiked = !isLiked;
      setIsLiked(nextLiked);
      setLikeCount((currentCount) => Math.max(0, currentCount + (nextLiked ? 1 : -1)));

      if (postId) {
        dispatch(likeOrUnlikePost(postId, activeProfileType));
        return;
      }

      axios.patch(`${server}/image/${imageId}/like`, { profileType: activeProfileType }, getAuthRequestConfig())
        .then((response) => applyImageEngagement(response.data))
        .catch((error) => {
          setIsLiked(!nextLiked);
          setLikeCount((currentCount) => Math.max(0, currentCount + (nextLiked ? -1 : 1)));
          setImageActionError(error.response?.data?.message || "Unable to update photo like.");
        });
    });
  };

  const handleOpenPostModal = (mode) => {
    if (!postId && !imageId && !(mode === "share" && image.url)) {
      return;
    }

    requireLogin(() => {
      if (postId) {
        setPostModalMode(mode);
        return;
      }

      if (mode === "share") {
        setImageShareDraft("");
        setPublishedImagePostId("");
        setImageActionError("");
      }

      setImageModalMode(mode);
    });
  };

  const handleShareImage = async () => {
    if (postId || imageId || image.url) {
      handleOpenPostModal("share");
      return;
    }

    if (!image.url || typeof navigator === "undefined") {
      return;
    }

    try {
      if (typeof navigator.share === "function") {
        await navigator.share({
          title: `${displayName} photo`,
          text: getImageCaption(image) || "MATRIX photo",
          url: image.url,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(image.url);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Unable to share gallery image:", error);
      }
    }
  };

  const handleSubmitImageComment = async (event) => {
    event.preventDefault();

    if (!imageId || !commentDraft.trim()) {
      return;
    }

    setIsSubmittingComment(true);
    setImageActionError("");

    try {
      const response = await axios.post(`${server}/image/${imageId}/comments`, {
        commentText: commentDraft.trim(),
        profileType: activeProfileType,
      }, getAuthRequestConfig());
      applyImageEngagement(response.data);
      setCommentDraft("");
    } catch (error) {
      setImageActionError(error.response?.data?.message || "Unable to post photo comment.");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleConfirmImageShare = async () => {
    if (!image.url) {
      return;
    }

    setIsPublishingImageShare(true);
    setImageActionError("");

    try {
      const formData = new FormData();
      formData.append("description", imageShareDraft.trim());
      formData.append("currentProfile", activeProfileName || "User");
      formData.append("mediaPath", image.url);

      if (activeProfileType === "User" && user?._id) {
        formData.append("userId", user._id);
      } else if (activeProfileType === "Shop" && seller?._id) {
        formData.append("userId", seller._id);
      }

      const postResponse = await axios.post(`${server}/posts/create-post`, formData, {
        ...getAuthRequestConfig(),
        headers: {
          ...getAuthRequestConfig().headers,
          "Content-Type": "multipart/form-data",
        },
      });

      if (postResponse.data?._id) {
        setPublishedImagePostId(postResponse.data._id);
      }

      if (imageId) {
        const response = await axios.post(`${server}/image/${imageId}/share`, {}, getAuthRequestConfig());
        applyImageEngagement(response.data);
      } else {
        setShareCount((currentCount) => currentCount + 1);
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }

      setImageActionError(error.response?.data?.message || "Unable to post shared photo.");
    } finally {
      setIsPublishingImageShare(false);
    }
  };

  const sourcePostModal = useMemo(() => {
    if (!postModalMode || !postId || typeof document === "undefined") {
      return null;
    }

    const ownerId = getImageOwnerId(image);
    const caption = getImageCaption(image);
    const createdAt = image.postCreatedAt || image.createdAt || new Date().toISOString();

    return ReactDOM.createPortal(
      <div className="post-modal" onClick={() => setPostModalMode(null)}>
        <div
          className="post-modal-content"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Photo source post"
        >
          <button
            className="post-modal-close-button"
            onClick={() => setPostModalMode(null)}
            aria-label="Close Modal"
          >
            &times;
          </button>
          <SinglePostWidget
            postId={postId}
            postUserId={ownerId}
            owner={{ _id: ownerId, name: displayName, avatar: { url: displayAvatar } }}
            name={displayName}
            description={caption}
            profileType={image.postProfileType || profileType}
            location={image.location || ""}
            images={image.url ? [{ url: image.url }] : []}
            videos={[]}
            avatar={displayAvatar}
            createdAt={createdAt}
            updatedAt={image.postUpdatedAt || image.updatedAt}
            link={image.link}
            initialIsComments={postModalMode === "comments"}
            initialShowReshareInput={postModalMode === "share"}
          />
        </div>
      </div>,
      document.body
    );
  }, [displayAvatar, displayName, image, postId, postModalMode, profileType]);

  const imageEngagementModal = useMemo(() => {
    if (!imageModalMode || typeof document === "undefined") {
      return null;
    }

    if (imageModalMode !== "share" && !imageId) {
      return null;
    }

    const isShareMode = imageModalMode === "share";

    return ReactDOM.createPortal(
      <div className="profile-image-action-modal-backdrop" onClick={() => setImageModalMode(null)}>
        <div
          className="profile-image-action-modal"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={isShareMode ? "Share photo" : "Photo comments"}
        >
          <button
            type="button"
            className="profile-image-action-modal-close"
            onClick={() => setImageModalMode(null)}
            aria-label="Close photo action"
          >
            &times;
          </button>
          <div className="profile-image-action-modal-header">
            <span>{isShareMode ? "Share Photo" : "Photo Comments"}</span>
            <strong>{displayName}</strong>
          </div>
          {isShareMode ? (
            <div className="profile-image-action-share-panel profile-image-post-share-panel">
              <textarea
                value={imageShareDraft}
                onChange={(event) => setImageShareDraft(event.target.value)}
                placeholder="Add a thought to your share..."
                rows={3}
                className="profile-image-post-share-textarea"
              />
              <article className="profile-image-post-share-card" aria-label="Shared photo post preview">
                <div className="post-widget-reshare-header profile-image-post-share-header">
                  {activeProfileAvatar ? (
                    <img src={activeProfileAvatar} alt="" className="post-widget-reshare-avatar" />
                  ) : (
                    <span className="post-widget-reshare-avatar profile-image-post-share-avatar-fallback">
                      {String(activeProfileDisplayName || "BV").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="post-widget-reshare-by">
                    <span className="post-widget-reshare-label">Sharing as</span>
                    <span className="post-widget-reshare-name">{activeProfileDisplayName}</span>
                  </span>
                </div>
                {imageShareDraft.trim() ? (
                  <p className="post-widget-reshare-description profile-image-post-share-copy">{imageShareDraft.trim()}</p>
                ) : null}
                <div className="profile-image-post-share-source">
                  <div className="profile-image-post-share-source-meta">
                    {displayAvatar ? <img src={displayAvatar} alt="" /> : <span>{String(displayName || "BV").charAt(0).toUpperCase()}</span>}
                    <strong>{displayName}</strong>
                  </div>
                  {getImageCaption(image) ? <p>{getImageCaption(image)}</p> : null}
                  {image.url ? <img src={image.url} alt="Shared photo preview" className="profile-image-post-share-image" /> : null}
                </div>
                <div className="profile-image-post-share-actions" aria-label="Shared post engagement preview">
                  <button type="button" disabled>
                    <ProfileGalleryPostActionIcon type="like" active={likeCount > 0} />
                    <span>{likeCount}</span>
                  </button>
                  <button type="button" disabled>
                    <ProfileGalleryPostActionIcon type="comment" active={commentCount > 0} />
                    <span>{commentCount}</span>
                  </button>
                  <button type="button" disabled>
                    <ProfileGalleryPostActionIcon type="share" />
                    <span>{shareCount}</span>
                  </button>
                </div>
              </article>
              {publishedImagePostId ? (
                <a className="profile-image-post-share-open-link" href={`/post/${publishedImagePostId}`}>
                  Open Shared Post
                </a>
              ) : (
                <button type="button" onClick={handleConfirmImageShare} disabled={isPublishingImageShare || !image.url}>
                  {isPublishingImageShare ? "Posting..." : "Post Photo"}
                </button>
              )}
            </div>
          ) : (
            <div className="profile-image-action-comments-panel">
              <div className="profile-image-action-comments-list">
                {imageComments.length > 0 ? imageComments.map((comment) => (
                  <article className="profile-image-action-comment" key={comment._id || `${comment.name}-${comment.createdAt}`}>
                    {comment.avatar ? <img src={comment.avatar} alt="" /> : <span>{String(comment.name || "BV").charAt(0).toUpperCase()}</span>}
                    <div>
                      <strong>{comment.name || "MATRIX member"}</strong>
                      <p>{comment.commentText}</p>
                    </div>
                  </article>
                )) : <p className="profile-image-action-empty">No comments yet.</p>}
              </div>
              <form className="profile-image-action-comment-form" onSubmit={handleSubmitImageComment}>
                <textarea
                  value={commentDraft}
                  onChange={(event) => setCommentDraft(event.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                />
                <button type="submit" disabled={isSubmittingComment || !commentDraft.trim()}>
                  {isSubmittingComment ? "Posting..." : "Post Comment"}
                </button>
              </form>
            </div>
          )}
          {imageActionError ? <p className="profile-image-action-error">{imageActionError}</p> : null}
        </div>
      </div>,
      document.body
    );
  }, [activeProfileAvatar, activeProfileDisplayName, commentCount, commentDraft, displayAvatar, displayName, image, imageActionError, imageComments, imageId, imageModalMode, imageShareDraft, isPublishingImageShare, isSubmittingComment, likeCount, publishedImagePostId, shareCount]);

  return (
    <>
      <div className={className} aria-label={ariaLabel}>
        <button
          type="button"
          className={buttonClassName}
          disabled={!postId && !imageId}
          onClick={handleLikePost}
          aria-label={postId ? "Like original post" : "Like photo"}
        >
          <ProfileGalleryPostActionIcon type="like" active={isLiked || likeCount > 0} />
          <span>{likeCount}</span>
        </button>
        <button
          type="button"
          className={buttonClassName}
          disabled={!postId && !imageId}
          onClick={() => handleOpenPostModal("comments")}
          aria-label={postId ? "Open original post comments" : "Open photo comments"}
        >
          <ProfileGalleryPostActionIcon type="comment" active={commentCount > 0} />
          <span>{commentCount}</span>
        </button>
        <button
          type="button"
          className={buttonClassName}
          onClick={handleShareImage}
          aria-label={postId ? "Share original post" : "Share photo"}
        >
          <ProfileGalleryPostActionIcon type="share" />
          <span>{shareCount}</span>
        </button>
      </div>
      {sourcePostModal}
      {imageEngagementModal}
      {showAuthPrompt && typeof document !== "undefined"
        ? ReactDOM.createPortal(
          <div className="auth-modal-backdrop" onClick={() => setShowAuthPrompt(false)}>
            <div className="auth-modal-content" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
              <AuthenticationPrompt forceVisible />
            </div>
          </div>,
          document.body
        )
        : null}
    </>
  );
};

export default ProfileGalleryPostActions;